import { Field } from '@campfire/field/lib';
import { TabletButton } from '@campfire/tablet-button/lib';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@material-ui/core';
import { Form, Formik, useFormikContext } from 'formik';
import React, { memo, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { object as YupObject, string as YupString } from 'yup';
import { FormBuilderValues } from '../FormBuilder';
import { PageBreakFormItemData } from '../FormBuilderDialogContext';

const validationSchema = YupObject().shape({
  title: YupString().required('Please give the page a name'),
});

interface Props {
  open: boolean;
  close: () => void;
  data: PageBreakFormItemData | null;
}

type PageBreakValues = {
  formItemId: string;
  title: string;
  order: number;
};

export const PageBreakFormItemDialog = memo((props: Props) => {
  const { open, close, data } = props;
  const formikContext = useFormikContext<FormBuilderValues>();
  const { values } = formikContext;
  const { formItems } = values;

  const title = useMemo(() => (data ? 'Edit Page' : 'Add Page Break'), [data]);

  const initialValues: PageBreakValues = useMemo(
    () => ({
      title: data?.title ?? '',
      order: data?.order ?? formItems.allIds.length,
      formItemId: data?.formItemId ?? `new-${uuidv4()}`,
      __typename: 'VOLUNTEER_FormItemPageBreakType',
    }),
    [data, open]
  );

  const handleSubmit = (vals: PageBreakValues) => {
    return data ? editPageBreak(vals) : addPageBreak(vals);
  };

  const addPageBreak = (vals: PageBreakValues) => {
    formikContext.setFieldValue('formItems', {
      ...formItems,
      allIds: formItems.allIds.concat(vals.formItemId),
      byId: {
        ...formItems.byId,
        [vals.formItemId]: vals,
      },
    });
    close();
  };

  const editPageBreak = (vals: PageBreakValues) => {
    formikContext.setFieldValue('formItems', {
      ...formItems,
      byId: {
        ...formItems.byId,
        [vals.formItemId]: vals,
      },
    });
    close();
  };

  const removePageBreak = () => {
    formikContext.setFieldValue('formItems', {
      ...formItems,
      allIds: formItems.allIds.filter((itemId) => itemId !== initialValues.formItemId),
    });
    close();
  };

  return (
    <Dialog open={open} maxWidth='sm' fullWidth>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ submitForm }) => (
          <Form>
            <DialogTitle>
              <Typography variant='h6'>{title}</Typography>
              <Typography variant='body2' color='textSecondary'>
                {'Everything after this break starts a new page. Pages can hold instructions only, with no questions.'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Field name='title' label='Page name' required fullWidth variant='outlined' helper autoFocus />
            </DialogContent>
            <DialogActions>
              <Grid container justify='flex-end'>
                {data ? (
                  <Grid item xs>
                    <TabletButton variant='outlined' color='error' onClick={removePageBreak}>
                      {'Delete'}
                    </TabletButton>
                  </Grid>
                ) : null}
                <Grid item>
                  <TabletButton onClick={close}>{'Cancel'}</TabletButton>
                </Grid>
                <Grid item>
                  <TabletButton type='button' color='primary' variant='contained' onClick={submitForm}>
                    {'Done'}
                  </TabletButton>
                </Grid>
              </Grid>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
});
